import React, {useMemo} from 'react';
import {defineMessages} from 'react-intl';
import {useNavigate} from 'react-router';
import type {Resource} from '@igp/shared/access-control';
import {defineAccessControlledResource} from '@igp/shared/access-control';
import {useAuthUser} from '@igp/shared/auth';
import {LayoutQueue, PageHeader, Toolbar, ToolbarItems, ToolbarItemsContainer} from '@igp/shared/components/layout';
import type {TabData} from '@igp/shared/components/tabs';
import {RoutingTabs} from '@igp/shared/components/tabs';
import type {ViewType} from '@igp/shared/redux/view';
import {getTodayDateRange} from '@igp/shared/utils';
import {EntityType} from '@models';

import {useBoAgentAccessCheck} from '@access-control';
import {agentPolicyRule} from '@policy-access';
import {routes} from '@config';
import type {PlayerLevelReportV2ViewModelKeys} from '@features/player-level-report-v2';
import {
    DataGridPlayerLevelReportV2,
    PlayerLevelReportV2DownloadCsvButton,
    PlayerLevelReportV2QueryFilters,
    usePlayerLevelReportV2,
    usePlayerLevelReportV2Settings,
} from '@features/player-level-report-v2';

import {
    playerLevelReportAllResource,
    playerLevelReportBasicResource,
    playerLevelReportCasinoResource,
    playerLevelReportGlobalSpinsResource,
    playerLevelReportPlayerActivityResource,
    playerLevelReportPlayerEcologyResource,
    playerLevelReportPokerMttResource,
    playerLevelReportPokerNhleSdResource,
    playerLevelReportPokerPloResource,
    playerLevelReportPokerResource,
    playerLevelReportShareResource,
    playerLevelReportSportsResource,
} from './permissions';

const localized = defineMessages({
    title: {id: 'playerLevelReport_Title', defaultMessage: 'Player Level Report'},
    share: {id: 'playerLevelReport_Share', defaultMessage: 'Commissionable Amount'},
    poker: {id: 'playerLevelReport_AllPoker', defaultMessage: 'All Poker'},
    pokerNhleSd: {id: 'playerLevelReport_PokerNhleSd', defaultMessage: 'Poker NLHE/SD'},
    pokerMtt: {id: 'playerLevelReport_PokerMtt', defaultMessage: 'Poker MTT'},
    pokerPlo: {id: 'playerLevelReport_PokerPlo', defaultMessage: 'Poker PLO'},
    casino: {id: 'playerLevelReport_Casino', defaultMessage: 'Casino'},
    globalSpins: {id: 'playerLevelReport_GlobalSpins', defaultMessage: 'Global Spins'},
    sports: {id: 'playerLevelReport_Sports', defaultMessage: 'Sports'},
    basic: {id: 'playerLevelReport_Basic', defaultMessage: 'Basic'},
    playerActivity: {id: 'playerLevelReport_PlayerActivity', defaultMessage: 'Player Activity'},
    playerEcology: {id: 'playerLevelReport_PlayerEcology', defaultMessage: 'Player Ecology'},
    all: {id: 'playerLevelReport_All', defaultMessage: 'All'},
});

type PlayerLevelReportProps = {
    columnsSettingsViewType: ViewType;
    tab: TabKey;
    hasTotalRow?: boolean;
};

export type TabKey =
    | 'basic'
    | 'casino'
    | 'globalSpins'
    | 'playerActivity'
    | 'playerEcology'
    | 'poker'
    | 'pokerMtt'
    | 'pokerNhleSd'
    | 'pokerPlo'
    | 'share'
    | 'sports'
    | 'all';

export function PlayerLevelReportBase({columnsSettingsViewType, tab, hasTotalRow = true}: PlayerLevelReportProps) {
    const navigate = useNavigate();
    const filtersViewType: ViewType = 'PlayerLevelReport';
    const isCasinoAvailable = useBoAgentAccessCheck(playerLevelReportCasinoResource);
    const isBasicAvailable = useBoAgentAccessCheck(playerLevelReportBasicResource);
    const isGlobalSpinsAvailable = useBoAgentAccessCheck(playerLevelReportGlobalSpinsResource);
    const isPlayerActivityAvailable = useBoAgentAccessCheck(playerLevelReportPlayerActivityResource);
    const isPlayerEcologyAvailable = useBoAgentAccessCheck(playerLevelReportPlayerEcologyResource);
    const isPokerAvailable = useBoAgentAccessCheck(playerLevelReportPokerResource);
    const isPokerMttAvailable = useBoAgentAccessCheck(playerLevelReportPokerMttResource);
    const isPokerNhleSdAvailable = useBoAgentAccessCheck(playerLevelReportPokerNhleSdResource);
    const isPokerPloAvailable = useBoAgentAccessCheck(playerLevelReportPokerPloResource);
    const isShareAvailable = useBoAgentAccessCheck(playerLevelReportShareResource);
    const isSportsAvailable = useBoAgentAccessCheck(playerLevelReportSportsResource);
    const isAllAvailable = useBoAgentAccessCheck(playerLevelReportAllResource);

    const tabs: TabData[] = useMemo(
        () =>
            [
                isShareAvailable ? {value: routes.playerLevelReportShare, title: localized.share} : null,
                isBasicAvailable ? {value: routes.playerLevelReportBasic, title: localized.basic} : null,
                isPokerAvailable ? {value: routes.playerLevelReportPoker, title: localized.poker} : null,
                isPokerNhleSdAvailable ? {value: routes.playerLevelReportPokerNhleSd, title: localized.pokerNhleSd} : null,
                isPokerMttAvailable ? {value: routes.playerLevelReportPokerMtt, title: localized.pokerMtt} : null,
                isPokerPloAvailable ? {value: routes.playerLevelReportPokerPlo, title: localized.pokerPlo} : null,
                isCasinoAvailable ? {value: routes.playerLevelReportCasino, title: localized.casino} : null,
                isGlobalSpinsAvailable ? {value: routes.playerLevelReportGlobalSpins, title: localized.globalSpins} : null,
                isSportsAvailable ? {value: routes.playerLevelReportSports, title: localized.sports} : null,
                isPlayerActivityAvailable ? {value: routes.playerLevelReportPlayerActivity, title: localized.playerActivity} : null,
                isPlayerEcologyAvailable ? {value: routes.playerLevelReportPlayerEcology, title: localized.playerEcology} : null,
                isAllAvailable ? {value: routes.playerLevelReportAll, title: localized.all} : null,
            ].filter(t => t),
        []
    );
    const todayDateRange = getTodayDateRange();
    const commonColumns: PlayerLevelReportV2ViewModelKeys[] = [
        'uid',
        'username',
        'referrer_agent_id',
        'referrer_agent_username',
        'iso_alpha2_country_code',
    ];
    const tabsFields: Record<TabKey, PlayerLevelReportV2ViewModelKeys[]> = {
        basic: [
            'poker_summary.poker_share',
            'poker_mtt_summary.mtt_ggr',
            'casino_summary.casino_ggr',
            'casino_summary.casino_wager',
            'global_spins_summary.global_spins_ggr',
            'sports_summary.sports_ggr',
            'sports_summary.sports_settled_bet',
            'player_activity_summary.player_win_loss',
            'player_activity_summary.cash_game_hands_played',
            'player_activity_summary.mtt_played',
        ],
        casino: [
            'casino_summary.casino_ggr',
            'casino_summary.casino_ngr',
            'casino_summary.internal_casino_ggr',
            'casino_summary.external_casino_ggr',
            'casino_summary.casino_bonus',
            'casino_summary.casino_share',
            'casino_summary.casino_wager',
        ],
        globalSpins: ['global_spins_summary.global_spins_expected_ggr', 'global_spins_summary.cost_global_spins_tickets'],
        playerActivity: [
            'player_activity_summary.player_win_loss',
            'player_activity_summary.cash_game_hands_played',
            'player_activity_summary.mtt_played',
            'player_activity_summary.net_deposit',
            'bonus_cost_summary.cash_realized_bonus',
            'bonus_cost_summary.cost_ftd_bonus_pro',
            'bonus_cost_summary.psp_fee',
            'bonus_cost_summary.cost_psp_fee_pro',
        ],
        playerEcology: [
            'player_ecology.stake_2_is_pro_nlhe',
            'player_ecology.stake_4_is_pro_nlhe',
            'player_ecology.stake_10_is_pro_nlhe',
            'player_ecology.stake_20_is_pro_nlhe',
            'player_ecology.stake_50_is_pro_nlhe',
            'player_ecology.stake_100_is_pro_nlhe',
            'player_ecology.stake_200_is_pro_nlhe',
            'player_ecology.stake_400_is_pro_nlhe',
            'player_ecology.is_pro_shortdeck',
            'player_ecology.is_pro_zoom',
            'player_ecology.ecology_flag',
        ],
        poker: [
            'poker_summary.poker_ggr',
            'poker_summary.poker_rake',
            'poker_summary.poker_ngr',
            'poker_summary.poker_addons',
            'poker_summary.poker_addons_add_on',
            'poker_summary.poker_addons_guess',
            'poker_summary.poker_addons_insurance',
            'poker_summary.poker_ggr_usd_pro_ecology_modified',
            'poker_summary.poker_share',
            'bonus_cost_summary.poker_bonus',
        ],
        pokerMtt: [
            'poker_mtt_summary.mtt_ggr',
            'poker_mtt_summary.mtt_ngr',
            'poker_mtt_summary.player_mtt_win_loss',
            'bonus_cost_summary.mtt_ticket_bonus',
            'poker_mtt_summary.mtt_total_buyin',
            'bonus_cost_summary.cost_tournament_overlay',
            'bonus_cost_summary.cost_tournament_overlay_excl_freeroll',
        ],
        pokerNhleSd: [
            'poker_nlhe_summary.player_nlhe_shortdeck_win_loss_prev_180_days',
            'poker_nlhe_summary.ggr_poker_prev_180_days',
            'poker_nlhe_summary.player_nlhe_shortdeck_win_loss_lifetime',
            'poker_nlhe_summary.nlhe_ggr_usd_pro',
            'poker_nlhe_summary.nlhe_rakes_ggr_usd_pro',
            'poker_nlhe_summary.nlhe_add_on_ggr_usd_pro',
            'poker_nlhe_summary.nlhe_insurance_ggr_usd_pro',
            'poker_nlhe_summary.nlhe_jackpot_ggr_usd_pro',
            'poker_nlhe_summary.shortdeck_ggr_usd_pro',
            'poker_nlhe_summary.shortdeck_rakes_ggr_usd_pro',
            'poker_nlhe_summary.shortdeck_add_on_ggr_usd_pro',
            'poker_nlhe_summary.shortdeck_insurance_ggr_usd_pro',
            'poker_nlhe_summary.nlhe_micro_ggr_usd_pro',
            'poker_nlhe_summary.nlhe_low_ggr_usd_pro',
            'poker_nlhe_summary.nlhe_medium_ggr_usd_pro',
            'poker_nlhe_summary.nlhe_high_ggr_usd_pro',
        ],
        pokerPlo: ['poker_plo_summary.plo_ggr'],
        share: [
            'poker_summary.poker_share',
            'poker_mtt_summary.mtt_ggr',
            'poker_plo_summary.plo_ggr',
            'casino_summary.casino_share',
            'casino_summary.casino_wager',
            'global_spins_summary.global_spins_expected_ggr',
            'sports_summary.sports_ggr',
            'sports_summary.sports_settled_bet',
        ],
        sports: ['sports_summary.sports_settled_bet', 'sports_summary.sports_ggr'],
        all: [
            'total_ggr_summary.ggr',
            'poker_summary.poker_share',
            'poker_mtt_summary.mtt_ggr',
            'poker_plo_summary.plo_ggr',
            'casino_summary.casino_share',
            'casino_summary.casino_wager',
            'global_spins_summary.global_spins_expected_ggr',
            'sports_summary.sports_ggr',
            'sports_summary.sports_settled_bet',
            'poker_summary.poker_ggr',
            'poker_summary.poker_rake',
            'poker_summary.poker_ngr',
            'poker_summary.poker_addons',
            'poker_summary.poker_addons_add_on',
            'poker_summary.poker_addons_guess',
            'poker_summary.poker_addons_insurance',
            'poker_summary.poker_ggr_usd_pro_ecology_modified',
            'bonus_cost_summary.poker_bonus',
            'poker_nlhe_summary.player_nlhe_shortdeck_win_loss_prev_180_days',
            'poker_nlhe_summary.ggr_poker_prev_180_days',
            'poker_nlhe_summary.player_nlhe_shortdeck_win_loss_lifetime',
            'poker_nlhe_summary.nlhe_ggr_usd_pro',
            'poker_nlhe_summary.nlhe_rakes_ggr_usd_pro',
            'poker_nlhe_summary.nlhe_add_on_ggr_usd_pro',
            'poker_nlhe_summary.nlhe_insurance_ggr_usd_pro',
            'poker_nlhe_summary.nlhe_jackpot_ggr_usd_pro',
            'poker_nlhe_summary.shortdeck_ggr_usd_pro',
            'poker_nlhe_summary.shortdeck_rakes_ggr_usd_pro',
            'poker_nlhe_summary.shortdeck_add_on_ggr_usd_pro',
            'poker_nlhe_summary.shortdeck_insurance_ggr_usd_pro',
            'poker_nlhe_summary.nlhe_micro_ggr_usd_pro',
            'poker_nlhe_summary.nlhe_low_ggr_usd_pro',
            'poker_nlhe_summary.nlhe_medium_ggr_usd_pro',
            'poker_nlhe_summary.nlhe_high_ggr_usd_pro',
            'poker_mtt_summary.mtt_ngr',
            'poker_mtt_summary.player_mtt_win_loss',
            'poker_mtt_summary.mtt_ticket_bonus',
            'poker_mtt_summary.mtt_total_buyin',
            'bonus_cost_summary.cost_tournament_overlay',
            'bonus_cost_summary.cost_tournament_overlay_excl_freeroll',
            'casino_summary.casino_ggr',
            'casino_summary.casino_ngr',
            'casino_summary.internal_casino_ggr',
            'casino_summary.external_casino_ggr',
            'casino_summary.casino_bonus',
            'global_spins_summary.cost_global_spins_tickets',
            'player_activity_summary.player_win_loss',
            'player_activity_summary.cash_game_hands_played',
            'player_activity_summary.mtt_played',
            'player_activity_summary.net_deposit',
            'bonus_cost_summary.cash_realized_bonus',
            'bonus_cost_summary.cost_ftd_bonus_pro',
            'bonus_cost_summary.psp_fee',
            'bonus_cost_summary.cost_psp_fee_pro',
            'player_ecology.stake_2_is_pro_nlhe',
            'player_ecology.stake_4_is_pro_nlhe',
            'player_ecology.stake_10_is_pro_nlhe',
            'player_ecology.stake_20_is_pro_nlhe',
            'player_ecology.stake_50_is_pro_nlhe',
            'player_ecology.stake_100_is_pro_nlhe',
            'player_ecology.stake_200_is_pro_nlhe',
            'player_ecology.stake_400_is_pro_nlhe',
            'player_ecology.is_pro_shortdeck',
            'player_ecology.is_pro_zoom',
            'player_ecology.ecology_flag',
        ],
    };

    const allCustomTabsFields: PlayerLevelReportV2ViewModelKeys[] = [...new Set(Object.values(tabsFields).flat())];
    const pinnedColumns: PlayerLevelReportV2ViewModelKeys[] = ['uid', 'username'];
    const columns = [...commonColumns, ...tabsFields[tab]];

    const allTabsFields: PlayerLevelReportV2ViewModelKeys[] = [...commonColumns, ...allCustomTabsFields];
    const {
        items,
        rawItems,
        summary,
        summaryRaw,
        totalCount,
        searchFilter,
        filterString,
        handleFilterChange,
        handlePageChange,
        handlePageSizeChange,
        handleSortChange,
    } = usePlayerLevelReportV2({
        viewType: filtersViewType,
        fields: allTabsFields,
        defaultPaging: {page: 1, pageSize: 25},
        defaultFilters: [
            {key: 'started_at_from', value: todayDateRange[0]},
            {key: 'started_at_to', value: todayDateRange[1]},
        ],
        defaultSorting: [{field: 'uid', sort: 'desc'}],
        validateFilter: () => true,
        hasSummary: true,
        syncWithUrl: true,
    });
    const {visibleColumns, onVisibleColumnsChange} = usePlayerLevelReportV2Settings({
        viewType: columnsSettingsViewType,
        defaultColumns: columns,
        pinnedColumns,
    });
    const boAgentPlayerId = useAuthUser()?.playerId as string;
    const isAgent = useBoAgentAccessCheck(playerLevelReportAgentResource);

    function handleTabOpen(path: string) {
        navigate(path);
    }
    return (
        <LayoutQueue
            header={
                <>
                    <PageHeader
                        header={localized.title}
                        actions={
                            <PlayerLevelReportV2DownloadCsvButton
                                mode="server"
                                keys={visibleColumns}
                                filter={filterString}
                                filename={localized.title}
                                total={totalCount}
                                disabled={!totalCount}
                            />
                        }
                        removeBottomSpacing
                    />
                    <RoutingTabs tabs={tabs} onOpenTab={handleTabOpen} />
                </>
            }
            toolbar={
                <Toolbar>
                    <ToolbarItemsContainer primary>
                        <ToolbarItems streched>
                            <PlayerLevelReportV2QueryFilters
                                model={filterString}
                                onChange={handleFilterChange}
                                availableFilters={['uid_username', 'agent', 'countries', 'startedAt']}
                                countryEntity={EntityType.Country}
                                agentProfileEntity={EntityType.AgentProfile}
                                viewType={filtersViewType}
                                defaultPlayerId={boAgentPlayerId}
                                isAgent={isAgent}
                            />
                        </ToolbarItems>
                    </ToolbarItemsContainer>
                </Toolbar>
            }
            body={
                <DataGridPlayerLevelReportV2
                    configurableColumns={{pinnedColumns, visibleColumns, onVisibleColumnsChange}}
                    columns={columns}
                    rows={items}
                    rawModels={rawItems}
                    pinnedRow={items?.length && hasTotalRow ? summary : null}
                    pinnedRawModel={summaryRaw}
                    paging={{
                        page: searchFilter?.paging?.page,
                        pageSize: searchFilter?.paging?.pageSize,
                        rowCount: totalCount,
                    }}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    sortModel={{sortModel: searchFilter?.sorting}}
                    onSortChange={handleSortChange}
                    frozenColumnsCount={{desktop: 2}}
                />
            }
        />
    );
}

const playerLevelReportAgentResource: Resource = defineAccessControlledResource({
    name: 'policyResources_agent',
    rules: agentPolicyRule,
});
